const TOKEN_SYMBOLS = new Set(["(", ")", "[", "]", ":", "@", ",", "!"]);

export function tokenizeSearchExpressionDsl(input) {
  const source = String(input ?? "");
  const tokens = [];
  let index = 0;
  while (index < source.length) {
    const char = source[index];
    if (/\s/.test(char)) {
      index += 1;
      continue;
    }
    if (TOKEN_SYMBOLS.has(char)) {
      tokens.push({ type: "symbol", value: char, start: index, end: index + 1 });
      index += 1;
      continue;
    }
    if (char === "\"" || char === "'") {
      const start = index;
      const quote = char;
      let value = "";
      index += 1;
      let closed = false;
      while (index < source.length) {
        const next = source[index];
        if (next === "\\") {
          const escaped = source[index + 1];
          if (escaped != null) {
            value += escaped;
            index += 2;
            continue;
          }
        }
        if (next === quote) {
          index += 1;
          closed = true;
          break;
        }
        value += next;
        index += 1;
      }
      tokens.push({ type: "value", value, start, end: index, closed });
      continue;
    }
    const start = index;
    let end = index;
    while (
      end < source.length &&
      !/\s/.test(source[end]) &&
      !TOKEN_SYMBOLS.has(source[end]) &&
      source[end] !== "\"" &&
      source[end] !== "'"
    ) {
      end += 1;
    }
    tokens.push({ type: "word", value: source.slice(index, end), start, end });
    index = end;
  }
  return tokens;
}

export function searchExpressionTokenText(token) {
  return String(token?.value ?? "");
}

export function searchExpressionTokenLower(token) {
  return searchExpressionTokenText(token).toLowerCase();
}

export function searchExpressionTokenIsValue(token) {
  return token?.type === "word" || token?.type === "value";
}

export function searchExpressionTokenIsStandaloneOperator(token) {
  if (token?.type !== "word") {
    return false;
  }
  const lowered = searchExpressionTokenLower(token);
  return lowered === "and" || lowered === "or" || lowered === "not";
}

export function searchExpressionMissingAliasLength(tokens, index) {
  const first = searchExpressionTokenLower(tokens?.[index]);
  const second = searchExpressionTokenLower(tokens?.[index + 1]);
  const third = searchExpressionTokenLower(tokens?.[index + 2]);
  if (first !== "not") {
    return 0;
  }
  if (second === "yet" && third === "caught") {
    return 3;
  }
  return second === "caught" ? 2 : 0;
}

function searchExpressionTokenIsSeparator(token) {
  return /^[^\p{L}\p{N}]+$/u.test(searchExpressionTokenText(token));
}

export function searchExpressionSpacedValueCanExtend({
  source,
  firstToken,
  previousToken,
  previousEnd,
  token,
  nextToken,
}) {
  const gap = String(source ?? "").slice(previousEnd, token?.start);
  return !/[\n()]/u.test(gap) &&
    searchExpressionTokenIsValue(token) &&
    !searchExpressionTokenIsStandaloneOperator(token) &&
    !(
      token?.type === "word" &&
      nextToken?.type === "symbol" &&
      nextToken.value === ":"
    ) &&
    (
      searchExpressionTokenIsSeparator(previousToken) ||
      searchExpressionTokenLower(token) !== searchExpressionTokenLower(firstToken)
    );
}

export function spanForSearchExpressionToken(token, fallback = 0) {
  const start = Number.isInteger(token?.start) ? token.start : fallback;
  const end = Number.isInteger(token?.end) ? token.end : start;
  return { start, end };
}

export function normalizeSearchExpressionSpan(span, source) {
  const sourceLength = String(source ?? "").length;
  const start = Math.max(0, Math.min(Number.parseInt(span?.start, 10) || 0, sourceLength));
  const parsedEnd = Number.parseInt(span?.end, 10);
  const end = Math.max(start, Math.min(Number.isInteger(parsedEnd) ? parsedEnd : start, sourceLength));
  return { start, end };
}

export function searchExpressionRawSlice(source, span) {
  const normalized = normalizeSearchExpressionSpan(span, source);
  return String(source ?? "").slice(normalized.start, normalized.end);
}

export function makeCombinedSearchExpressionValueToken(source, firstToken, lastToken) {
  return {
    type: "value",
    value: String(source ?? "").slice(firstToken.start, lastToken.end),
    start: firstToken.start,
    end: lastToken.end,
    closed: true,
  };
}
